/**
 * Every month's two files as one searchable record — the supplier's answer to
 * "what did I send, when, and how did it score?".
 *
 * The cards above are the task surface; this is the record. Built on the shared
 * list controls (DESIGN-GUIDELINES.md §3.1/§3.3/§4/§8) so it searches, filters,
 * sorts, pages and exports the same way every other list in the app does.
 */
import { useCallback, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { FileSpreadsheet, Inbox } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import { ListPager, ListSearch, matchesSearch, useListControls } from '../../../components/common/listControls';
import { SortableTh } from '../../../components/common/SortableTh';
import type { SupplierUploadTracking } from '../../../lib/api/barratt';
import UploadStatusBadge from '../UploadStatusBadge';
import { DATASET_LABELS, datasetLabel, formatDay, formatOtif } from '../uploadCycle';
import type { ObligationPeriod } from './useMyObligations';
import { needsAction } from './uploadSummary';
import RowFileButton from './RowFileButton';

type StatusFilter = 'all' | 'uploaded' | 'outstanding';

interface UploadHistoryTableProps {
  periods: ObligationPeriod[];
  uploadingId: number | null;
  onUpload: (row: SupplierUploadTracking, file: File) => void;
  /** Goes in the export file name. */
  supplierName?: string;
}

/** A tracking row flattened with the month it belongs to. */
interface HistoryRow {
  row: SupplierUploadTracking;
  periodKey: string;
  periodLabel: string;
}

/** Says which filter emptied the table — "nothing uploaded" is wrong when only Quality is showing. */
function emptyMessage(status: StatusFilter, datasetName: string | null): string {
  const of = datasetName ? ` ${datasetName}` : '';
  if (status === 'uploaded') return datasetName ? `No${of} file has been uploaded yet` : 'Nothing has been uploaded yet';
  if (status === 'outstanding') {
    return datasetName ? `No${of} file is outstanding` : 'Nothing outstanding — every file asked for is in';
  }
  return datasetName ? `No${of} files on record yet` : 'No monthly files on record yet';
}

export default function UploadHistoryTable({
  periods,
  uploadingId,
  onUpload,
  supplierName,
}: UploadHistoryTableProps) {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [dataset, setDataset] = useState('');

  const allRows = useMemo<HistoryRow[]>(
    () =>
      periods.flatMap((period) =>
        period.rows.map((row) => ({ row, periodKey: period.key, periodLabel: period.label })),
      ),
    [periods],
  );

  // Months the customer never asked for are on record but not outstanding —
  // the banner has always excluded them, so the tab does too.
  const inStatus = useCallback(
    (row: SupplierUploadTracking, filter: StatusFilter) => {
      if (filter === 'uploaded') return row.status === 'uploaded';
      if (filter === 'outstanding') return needsAction(row);
      return true;
    },
    [],
  );

  const filtered = useMemo(
    () => allRows.filter(({ row }) => (!dataset || row.dataset_type === dataset) && inStatus(row, status)),
    [allRows, status, dataset, inStatus],
  );

  const searchText = useCallback(
    (r: HistoryRow) => [r.periodLabel, datasetLabel(r.row), r.row.status_display, r.row.file_name ?? ''].join(' '),
    [],
  );

  const list = useListControls<HistoryRow>(filtered, {
    searchText,
    sorters: {
      period: (r) => r.periodKey,
      dataset: (r) => datasetLabel(r.row),
      status: (r) => r.row.status,
      records: (r) => (r.row.status === 'uploaded' ? r.row.records_count : null),
      otif: (r) => (r.row.otif_pct === null ? null : Number(r.row.otif_pct)),
      ncrs: (r) => (r.row.ncr_count === null ? null : Number(r.row.ncr_count)),
      uploaded: (r) => r.row.uploaded_at ?? null,
      due: (r) => r.row.due_date,
    },
    defaultSort: { key: 'period', dir: 'desc' },
    tieBreak: (r) => datasetLabel(r.row),
    resetKeys: [status, dataset],
  });

  const exportExcel = useCallback(() => {
    const header = ['Month', 'Dataset', 'Status', 'Due', 'Uploaded', 'Rows', 'OTIF %', 'NCRs', 'File'];
    const body = list.orderedRows.map(({ row, periodLabel }) => [
      periodLabel,
      datasetLabel(row),
      row.status_display || row.status,
      formatDay(row.due_date),
      row.uploaded_at ? format(parseISO(row.uploaded_at), 'd MMM yyyy') : '',
      row.status === 'uploaded' ? row.records_count : '',
      row.otif_pct === null ? '' : Number(row.otif_pct),
      row.ncr_count === null ? '' : row.ncr_count,
      row.file_name ?? '',
    ]);
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Monthly uploads');
    const parts = [
      (supplierName || 'monthly').replace(/\s+/g, '-').toLowerCase(),
      status !== 'all' ? status : '',
      dataset,
      list.search ? 'search' : '',
    ].filter(Boolean);
    XLSX.writeFile(book, `${parts.join('-')}-uploads.xlsx`);
  }, [list.orderedRows, list.search, status, dataset, supplierName]);

  const datasetOptions = Object.entries(DATASET_LABELS).map(([value, label]) => ({ value, label }));
  const datasetName = dataset ? DATASET_LABELS[dataset as keyof typeof DATASET_LABELS] : null;

  // Counted after the dataset filter and the search, so a tab never promises
  // rows the other controls have already excluded: "Uploaded (20)" above a
  // single row is the tab lying about what clicking it does.
  const tabCounts = useMemo(() => {
    const scoped = allRows.filter(
      (r) => (!dataset || r.row.dataset_type === dataset) && matchesSearch(list.search, searchText(r)),
    );
    return {
      all: scoped.length,
      uploaded: scoped.filter((r) => inStatus(r.row, 'uploaded')).length,
      outstanding: scoped.filter((r) => inStatus(r.row, 'outstanding')).length,
    };
  }, [allRows, dataset, list.search, searchText, inStatus]);

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-lg">Upload history</CardTitle>
        <CardDescription>Every month on record, and what each file contained.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Tabs value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <TabsList data-testid="history-status-filter">
              <TabsTrigger value="all">All ({tabCounts.all})</TabsTrigger>
              <TabsTrigger value="uploaded">Uploaded ({tabCounts.uploaded})</TabsTrigger>
              <TabsTrigger value="outstanding">Outstanding ({tabCounts.outstanding})</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <div className="md:w-64">
              <ListSearch
                value={list.search}
                onChange={list.setSearch}
                placeholder="Search month, dataset, file…"
                testId="history-search"
              />
            </div>
            <div className="md:w-56" data-testid="history-dataset-filter">
              <SearchableSelect
                options={datasetOptions}
                value={dataset}
                onChange={setDataset}
                placeholder="All datasets"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exportExcel}
              disabled={list.orderedRows.length === 0}
              data-testid="history-export"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Export Excel
            </Button>
          </div>
        </div>

        {allRows.length === 0 ? (
          <div className="py-12 text-center" data-testid="history-empty">
            <Inbox className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{emptyMessage('all', null)}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="history-empty">
            <Inbox className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{emptyMessage(status, datasetName)}</p>
          </div>
        ) : list.orderedRows.length === 0 ? (
          <div className="py-12 text-center" data-testid="history-empty">
            <Inbox className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No months match "{list.search}"</p>
          </div>
        ) : (
          <>
            <ListPager list={list} position="top" noun="files" testIdPrefix="history" />
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTh field="period" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>
                      Month
                    </SortableTh>
                    <SortableTh field="dataset" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>
                      Dataset
                    </SortableTh>
                    <SortableTh field="status" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>
                      Status
                    </SortableTh>
                    <SortableTh field="due" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>
                      Due
                    </SortableTh>
                    <SortableTh field="uploaded" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>
                      Uploaded
                    </SortableTh>
                    <SortableTh field="records" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">
                      Rows
                    </SortableTh>
                    <SortableTh field="otif" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">
                      OTIF
                    </SortableTh>
                    <SortableTh field="ncrs" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">
                      NCRs
                    </SortableTh>
                    <TableHead>File</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.pageRows.map(({ row, periodLabel }) => (
                    <TableRow key={row.id} data-testid={`history-row-${row.dataset_type}-${row.id}`}>
                      <TableCell className="font-medium">{periodLabel}</TableCell>
                      <TableCell>{datasetLabel(row)}</TableCell>
                      <TableCell>
                        <UploadStatusBadge status={row.status} label={row.status_display} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{formatDay(row.due_date)}</TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {row.uploaded_at ? format(parseISO(row.uploaded_at), 'd MMM yyyy') : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.status === 'uploaded' ? row.records_count.toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatOtif(row.otif_pct)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.ncr_count === null ? '—' : row.ncr_count.toLocaleString()}
                      </TableCell>
                      <TableCell className="max-w-[16rem] truncate text-muted-foreground" title={row.file_name ?? undefined}>
                        {row.file_name || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Every row can take a file here: the cards above only
                            cover the month currently owed, so without this an
                            older gap — or a month nobody asked for — would be
                            visible in the record with no way to fill it. */}
                        <RowFileButton
                          row={row}
                          uploading={uploadingId === row.id}
                          disabled={uploadingId !== null && uploadingId !== row.id}
                          onUpload={(file) => onUpload(row, file)}
                          mode={row.status === 'uploaded' ? 'replace' : 'upload'}
                          context="table"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <ListPager list={list} position="bottom" noun="files" testIdPrefix="history" />
          </>
        )}
      </CardContent>
    </Card>
  );
}
